import { useCallback, useEffect, useRef, useState } from 'react'
import { Room } from 'livekit-client'
import { requestVoiceToken } from '@/services/voice/requestVoiceToken'
import { bindVoiceRoomEvents } from './bindVoiceRoomEvents'
import { publishVoiceChatEvent } from './publishVoiceChatEvent'
import { createVoiceOrchestrator } from './createVoiceOrchestrator'

export function useVoiceRoom({ conversationId, onTranscript }) {
  const roomRef = useRef(null)
  const onTranscriptRef = useRef(onTranscript)
  const detachAudioRef = useRef([])
  const orchestratorRef = useRef(null)
  const [state, setState] = useState({ status: 'idle', error: '', sentence: '' })
  useEffect(() => { onTranscriptRef.current = onTranscript }, [onTranscript])

  const stop = useCallback(async () => {
    const room = roomRef.current
    roomRef.current = null
    orchestratorRef.current?.stop()
    orchestratorRef.current = null
    detachAudioRef.current.splice(0).forEach((detach) => detach())
    if (room) room.disconnect()
    setState({ status: 'idle', error: '', sentence: '' })
  }, [])

  const start = useCallback(async () => {
    if (!conversationId || roomRef.current) return
    setState({ status: 'connecting', error: '', sentence: '' })
    try {
      const credentials = await requestVoiceToken(conversationId)
      const room = new Room({ adaptiveStream: true, dynacast: true })
      bindVoiceRoomEvents(room, { detachAudioRef, onTranscriptRef, orchestratorRef, setState, roomRef })
      await room.connect(credentials.url, credentials.token)
      roomRef.current = room
      orchestratorRef.current = createVoiceOrchestrator(room)
      await room.localParticipant.setMicrophoneEnabled(true)
      setState({ status: 'active', error: '', sentence: '' })
    } catch (error) {
      await stop()
      setState({ status: 'error', error: error instanceof Error ? error.message : 'Voice connection failed', sentence: '' })
    }
  }, [conversationId, stop])

  const publishChatEvent = useCallback((event) => {
    publishVoiceChatEvent(orchestratorRef.current, event)
  }, [])

  useEffect(() => () => { void stop() }, [conversationId, stop])
  return { ...state, start, stop, publishChatEvent, active: state.status === 'active' }
}
